import { useEffect, useCallback } from 'react';
import { useBlocker } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { openConfirmation, clearAllDirty } from '../redux/store';
import { selectIsDirty } from '../redux/selectors';

/**
 * Navigation guard hook that replaces manual isDirty checks.
 * Uses React Router's useBlocker to intercept navigation when there are unsaved changes,
 * and adds a beforeunload handler to catch browser close/refresh.
 *
 * Usage:
 *   useNavigationGuard();
 *
 * Relies on Redux `dirtyOwners` state (read via selectIsDirty) and the
 * existing ConfirmationModal component. When the user confirms leaving,
 * we drop the entire owner set — the destination route is responsible
 * for its own dirty tracking.
 *
 * React Router v7 only supports one active blocker per router instance.
 * Call this at most once per route subtree.
 */
const useNavigationGuard = () => {
  const dispatch = useDispatch();
  const isDirty = useSelector(selectIsDirty);

  // Block in-app navigation when dirty
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) =>
        isDirty && currentLocation.pathname !== nextLocation.pathname,
      [isDirty]
    )
  );

  // Show confirmation modal when blocker activates
  useEffect(() => {
    if (blocker.state === 'blocked') {
      dispatch(
        openConfirmation({
          message:
            'You have unsaved changes. Are you sure you want to leave? Changes may be lost if not saved.',
          type: 'CONFIRM_NAVIGATION',
          onConfirm: () => {
            dispatch(clearAllDirty());
            blocker.proceed();
          },
        })
      );
    }
  }, [blocker, dispatch]);

  // Handle browser close / refresh
  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  return blocker;
};

export default useNavigationGuard;
